import { useState } from 'react';
import { api } from '../lib/api';
import { int } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import { C, EASE_SPRING, ink } from '../lib/theme';
import type { Filters } from '../lib/types';
import { FalsePositiveModal } from './FalsePositiveModal';
import { IconFlag } from './icons';
import { originFrom, type Origin } from './ScaleModal';

/**
 * Botão flutuante da Zona de Falso Positivo — mini ícone discreto (não mais
 * uma pill de texto sempre visível, pedido explícito de deixar "mais
 * sutil"). Só ganha cor/glow/pulso quando existe pendência de verdade **no
 * período selecionado na tela** — antes buscava o KPI sem filtro nenhum,
 * então piscava mesmo quando a pendência era de um mês fora do filtro
 * ativo. `from`/`to` do filtro viajam pro backend (ver
 * `false-positives.service.ts` `PeriodFilter`), então o número já nasce
 * escopado, sem filtrar client-side.
 */
export function FalsePositiveButton({ filters }: { filters: Filters }) {
  const [origin, setOrigin] = useState<Origin | null>(null);
  const [open, setOpen] = useState(false);
  const period = { from: filters.from, to: filters.to };
  const kpis = useAsync(() => api.falsePositivesKpis(period), [open, filters.from, filters.to]);
  const pendentes = int(kpis.data?.pendentes_count);
  const alert = pendentes > 0;

  return (
    <>
      <button
        type="button"
        onClick={(e) => { setOrigin(originFrom(e)); setOpen(true); }}
        title={alert
          ? `${pendentes} falso positivo${pendentes === 1 ? '' : 's'} pra conferir no período selecionado`
          : 'Zona de falso positivo — churns que podem ter reassinado'}
        style={{
          position: 'fixed', right: 26, bottom: 26, zIndex: 120,
          width: 42, height: 42, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: `1px solid ${alert ? 'rgba(245,165,36,0.45)' : ink(0.12)}`,
          background: alert ? 'rgba(245,165,36,0.14)' : 'var(--glass-bg)',
          backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
          color: alert ? C.amber : ink(0.4),
          cursor: 'pointer', font: 'inherit',
          boxShadow: '0 8px 22px -10px rgba(8,8,16,0.55)',
          transition: `transform 0.2s ${EASE_SPRING}, color 0.2s, background 0.2s, border-color 0.2s`,
        }}
      >
        <IconFlag size={16} />
        {alert && (
          <span style={{
            position: 'absolute', top: -3, right: -3, minWidth: 16, height: 16, borderRadius: 99,
            padding: '0 4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 9.5, fontWeight: 800, color: '#1A1207', background: C.amber,
            border: `1.5px solid ${C.bgDeep}`, animation: 'koFpBlink 1.8s ease-in-out infinite',
          }}>{pendentes}</span>
        )}
      </button>

      <FalsePositiveModal open={open} origin={origin} onClose={() => setOpen(false)} period={period} />

      <style>{`
        @keyframes koFpBlink {
          0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(245,165,36,0.55); }
          50% { opacity: 0.75; box-shadow: 0 0 0 4px rgba(245,165,36,0); }
        }
      `}</style>
    </>
  );
}
